import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AnimalAvatar } from "@/components/animals/animal-avatar";

interface LinkedAnimalSummary {
  id: string;
  animalId: string;
  animalName: string | null;
  animalSpecies: string | null;
  animalPhotoUrl: string | null;
}

interface LinkedAnimalsTooltipProps {
  taskId: string;
  animalCount: number;
  children: React.ReactNode;
}

export function LinkedAnimalsTooltip({ taskId, animalCount, children }: LinkedAnimalsTooltipProps) {
  const [open, setOpen] = useState(false);

  const { data: animals } = useQuery<LinkedAnimalSummary[]>({
    queryKey: ["/api/tasks", taskId, "animals"],
    enabled: open && animalCount > 0,
    staleTime: 30_000,
  });

  if (animalCount === 0) return <>{children}</>;

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <span className="cursor-default" data-testid={`tooltip-trigger-animals-${taskId}`}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="p-2 max-w-[240px]">
        <div className="flex flex-col gap-1.5" data-testid={`tooltip-content-animals-${taskId}`}>
          {animals ? (
            animals.map((animal) => (
              <div key={animal.id} className="flex items-center gap-2">
                <AnimalAvatar
                  animalId={animal.animalId}
                  photoUrl={animal.animalPhotoUrl}
                  name={animal.animalName || animal.animalId}
                  species={animal.animalSpecies || "OTHER"}
                  className="h-6 w-6"
                />
                <span className="text-xs truncate">{animal.animalName || animal.animalId}</span>
              </div>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">Loading...</span>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
